// Imersivo 3D — entry point.
// Phase 1: shows the loading screen. The full app is wired in later phases.

import { App } from './app/App';
import { installApiAuth } from './app/apiClient';
import { LoadingScreen } from './ui/loadingScreen';
import { isWebGLAvailable, isInAppBrowser } from './app/webglSupport';
import { TourEmbedBridge, isTourEmbedMode } from './app/tourEmbed';
import type { RoomsConfig } from './app/types';

async function bootstrap(): Promise<void> {
  // Attach the optional API access token to /api/ requests (no-op unless
  // VITE_APP_ACCESS_TOKEN is set). Must run before any /api/ fetch fires.
  installApiAuth();

  const canvas = document.getElementById('application-canvas');
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error('application-canvas element missing or not a <canvas>');
  }

  // Hard requirement: a WebGL context. Detect its absence up front and show an
  // accurate message rather than letting PlayCanvas throw "WebGL not supported"
  // (which the catch below would render as a misleading "reload the page").
  // Most common cause: an in-app browser (Instagram/LinkedIn/etc.) with WebGL
  // disabled — there the fix is to reopen in a real browser, not reload.
  if (!isWebGLAvailable()) {
    const loading = new LoadingScreen();
    loading.show();
    if (isInAppBrowser()) {
      loading.setError(
        'Este preview 3D precisa de WebGL.',
        'Toque no menu (⋯) e escolha "Abrir no navegador" — Chrome ou Safari.',
      );
    } else {
      loading.setError(
        'Seu navegador não suporta WebGL.',
        'Atualize o navegador ou ative a aceleração de hardware para ver o tour 360°.',
      );
    }
    return;
  }

  // Demo Studio embed (?embed=1): the portal owns the config. Boot is blocked
  // on its arrival because every manager reads the config in App's constructor
  // — starting on the bundled boutique demo and swapping later would mean the
  // operator briefly places hotspots in the wrong apartment.
  let embed: TourEmbedBridge | null = null;
  let embedConfig: RoomsConfig | undefined;
  if (isTourEmbedMode()) {
    embed = new TourEmbedBridge();
    embed.start();
    const loading = new LoadingScreen();
    loading.show();
    loading.setStatus('Aguardando a configuração do tour…');
    try {
      embedConfig = await embed.waitForConfig();
    } catch {
      loading.setError(
        'O portal não enviou a configuração do tour.',
        'Feche e reabra o editor. Se persistir, verifique se o domínio deste visualizador está liberado no portal.',
      );
      return;
    }
  }

  // The parent's language, when it sent one, outranks this page's ?lang=.
  const embedLocale = embed?.getParentLocale() ?? undefined;
  const app = new App({ canvas, config: embedConfig, embed, locale: embedLocale });
  await app.start();

  // Expose for debugging in the browser console.
  (window as unknown as { __imersivo?: App }).__imersivo = app;

  // PWA + offline — register the service worker after the app boots so it
  // doesn't compete with first-paint asset fetches. sw.js precaches the app
  // shell + panoramas (see public/sw.js + scripts/generate-precache.mjs), so an
  // installed PWA runs fully offline on the Quest — no Pi/server.
  // Service workers require a secure context: https, OR localhost/127.0.0.1
  // over http (the sideloaded-localhost appliance path). Skipped otherwise
  // (e.g. plain http://<LAN-IP>, where WebXR is unavailable anyway).
  // Never in embed mode: the worker precaches the BUNDLED demo (useless to an
  // editor whose panoramas are signed portal URLs), and its controllerchange
  // handler reloads the page on the next deploy — mid-edit, that would throw
  // away whatever the operator has moved since the last autosave.
  const swSecure =
    window.location.protocol === 'https:' ||
    ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if ('serviceWorker' in navigator && swSecure && !isTourEmbedMode()) {
    // Auto-reload once when a NEW worker takes control (a fresh deploy). The
    // page has already rendered textures from the OLD worker's cache, so even
    // after the new worker precaches corrected panoramas the visitor would keep
    // seeing the stale ones until they manually reload. `controllerchange`
    // fires when the new sw.js calls clients.claim(); reload then to pick up the
    // fresh assets. Guarded so it only fires when a worker ALREADY controlled
    // the page (skips the first-ever install) and never loops.
    const hadController = Boolean(navigator.serviceWorker.controller);
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController || reloading) return;
      reloading = true;
      window.location.reload();
    });
    void navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('[imersivo] service worker registration failed', err);
    });
  }
}

bootstrap().catch((err) => {
  console.error('[imersivo] fatal bootstrap error', err);
  const status = document.getElementById('loading-status');
  if (status) {
    status.textContent = 'Erro ao iniciar — recarregue a página';
    status.style.color = '#ff6b6b';
  }
});
